import React, {
  ReactElement,
  useContext,
  useRef,
  createContext,
  ForwardedRef,
  forwardRef,
} from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import request from 'utils/api/request';
import { useUser } from './user';
import {
  SavedUserPaymentMethod,
  UserPaymentMethod,
} from 'types/PaymentMethods';
import { useAccount } from './account';
import { PaymentMethodsWidgetCallbacks } from 'components/PaymentMethodsWidget/PaymentMethodsWidget';

interface UseEnabledElementsResult {
  /** Filtered elements based on options */
  accountPaymentInstruments: UserPaymentMethod[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  isError: boolean;
  /** Error object if any */
  error: unknown;
  /** Refetch function */
  refetch: () => void;
}

export const useAccountPaymentInstruments = (): UseEnabledElementsResult => {
  const { provider } = useUser();
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  }: UseQueryResult<SavedUserPaymentMethod[], unknown> = useQuery(
    [],
    async () => {
      if (!provider?.enablePSPIntegrationsV2) {
        return [];
      }
      const response = await request<{
        data: SavedUserPaymentMethod[];
        status: number;
      }>({
        path: `/1/accounts/self/account-payment-instruments`,
        method: 'GET',
      });

      return response.data;
    },
    {
      staleTime: 1 * 60 * 1000,
      cacheTime: 2 * 60 * 1000,
    }
  );

  return {
    accountPaymentInstruments: data ?? [],
    isLoading,
    isError,
    error,
    refetch,
  };
};

const PaymentMethodsWidgetContext = createContext<
  { ref: ForwardedRef<PaymentMethodsWidgetCallbacks> } | undefined
>(undefined);

type PaymentMethodsWidgetProviderProps = {
  children: ReactElement;
};
export const PaymentMethodsWidgetProvider = forwardRef<
  PaymentMethodsWidgetCallbacks,
  PaymentMethodsWidgetProviderProps
>(({ children }, ref) => {
  return (
    <PaymentMethodsWidgetContext.Provider value={{ ref }}>
      {children}
    </PaymentMethodsWidgetContext.Provider>
  );
});

export const usePaymentMethodsWidgetConsumer = () => {
  return useContext(PaymentMethodsWidgetContext);
};
